<div class="container py-4 referral-request">
  <h2 class="page-title mb-4">Request a Referral</h2>

  <app-bookmark-banner page="referral-request" />

  @if (isLoading) {
    <div class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  }

  @if (loadError) {
    <div class="alert alert-danger" role="alert">{{ loadError }}</div>
  }

  @if (info; as data) {
    <!-- Shop summary -->
    <div class="card summary-card mb-4">
      <div class="card-body">
        <h4 class="card-title mb-1">{{ data.businessName }}</h4>
        <div class="text-muted">{{ data.domain }}</div>
        <p class="mt-3 mb-0">
          Request referrals from happy customers! They'll receive a thank-you message with a link
          to refer family and friends to your {{ brandService.brand.businessNoun }}.
        </p>
      </div>
    </div>

    <!-- Tabbed action card -->
    <div class="card ir-action-card">
      <ul class="nav nav-tabs ir-tabs" role="tablist">
        <li class="nav-item" role="presentation">
          <button
            type="button"
            class="nav-link"
            [class.active]="activeTab === 'email'"
            (click)="setTab('email')"
          >
            Email
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-mail">
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10" />
              <path d="M3 7l9 6l9 -6" />
            </svg>
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            type="button"
            class="nav-link"
            [class.active]="activeTab === 'text'"
            (click)="setTab('text')"
          >
            Text
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-message">
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M8 9h8" />
              <path d="M8 13h6" />
              <path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12" />
            </svg>
          </button>
        </li>
      </ul>

      <div class="card-body">
        <!-- Email tab -->
        @if (activeTab === 'email') {
          <form [formGroup]="emailForm" (ngSubmit)="submitEmail()">
            @let recipientEmailControl = emailForm.get('recipientEmail')!;

            <p class="text-muted mb-3">
              Send a referral request directly to your customer's email address.
            </p>

            @if (emailSuccess) {
              <div class="alert alert-success" role="alert">{{ emailSuccess }}</div>
            }
            @if (emailError) {
              <div class="alert alert-danger" role="alert">{{ emailError }}</div>
            }

            <div class="mb-3">
              <label class="form-label" for="field_recipientEmail">
                Customer email address <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                id="field_recipientEmail"
                class="form-control"
                formControlName="recipientEmail"
                autocomplete="off"
              />
              @if (recipientEmailControl.invalid && (recipientEmailControl.touched || emailError)) {
                @if (recipientEmailControl.errors?.['required']) {
                  <small class="form-text text-danger">This field is required.</small>
                }
                @if (recipientEmailControl.errors?.['singleEmail']) {
                  <small class="form-text text-danger">Please enter a valid email.</small>
                }
              }
            </div>

            <div class="message-preview mb-3">
              <div class="message-preview-header">Message preview</div>
              <div class="message-preview-meta">
                <span class="text-muted">Subject:</span> {{ emailSubject }}
              </div>
              <div class="message-preview-body" [innerHTML]="emailHtmlPreview"></div>
            </div>

            <button type="submit" class="btn btn-portal-accent" [disabled]="emailSubmitting">
              Send Email
              @if (emailSubmitting) {
                <span class="spinner-border spinner-border-sm ms-1" role="status"></span>
              }
            </button>
          </form>

          <hr class="my-4" />

          <h6 class="mb-2">Prefer to send from your own email?</h6>
          <p class="text-muted mb-3">
            Open a pre-filled message in your default mail client so you can send it personally.
          </p>
          <a
            class="btn btn-outline-secondary"
            [href]="mailtoHref"
            target="_blank"
            rel="noopener"
          >
            Open in My Email
          </a>
        }

        <!-- Text tab -->
        @if (activeTab === 'text') {
          <form [formGroup]="textForm" (ngSubmit)="submitText()">
            @let recipientPhoneControl = textForm.get('recipientPhone')!;

            <p class="text-muted mb-3">
              Send a referral request as a text message to your customer's phone.
            </p>

            @if (textSuccess) {
              <div class="alert alert-success" role="alert">{{ textSuccess }}</div>
            }
            @if (textError) {
              <div class="alert alert-danger" role="alert">{{ textError }}</div>
            }

            <div class="mb-3">
              <label class="form-label" for="field_recipientPhone">
                Customer phone number <span class="text-danger">*</span>
              </label>
              <input
                type="tel"
                id="field_recipientPhone"
                class="form-control"
                formControlName="recipientPhone"
                mask="(000) 000-0000"
                [showMaskTyped]="true"
                [dropSpecialCharacters]="false"
                placeholder="(555) 555-5555"
                autocomplete="off"
              />
              @if (recipientPhoneControl.invalid && (recipientPhoneControl.touched || textError)) {
                @if (recipientPhoneControl.errors?.['required']) {
                  <small class="form-text text-danger">This field is required.</small>
                }
                @if (recipientPhoneControl.errors?.['phone']) {
                  <small class="form-text text-danger">Please enter a valid 10-digit phone number.</small>
                }
              }
            </div>

            <div class="message-preview mb-3">
              <div class="message-preview-header">Message preview</div>
              <div class="message-preview-body message-preview-sms">{{ smsPreview }}</div>
            </div>

            <button type="submit" class="btn btn-portal-accent" [disabled]="textSubmitting">
              Send Text
              @if (textSubmitting) {
                <span class="spinner-border spinner-border-sm ms-1" role="status"></span>
              }
            </button>
          </form>
        }

      </div>
    </div>
  }
</div>
